import { Mailbox } from "@phosphor-icons/react"
import { Button } from "@tokenterminal/core/ui/button/Button"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { TextField } from "@tokenterminal/core/ui/input/TextField"
import { Card } from "@tokenterminal/core/ui/layout/card/Card"
import { useCallback, useMemo, useState } from "react"
import MarketingHeading from "@/components/common/MarketingHeading"
import { subscribeToNewsLetter } from "@/components/resources/data/api/newsletter"

const defaultData = {
  tag: "Weekly newsletter",
  title: "Data-driven research on crypto",
  subtitle: "Insights without noise for the fundamentals focused investor.",
  description: `Our mission is to help you focus on what matters. We provide weekly, curated, data-driven overviews of key market developments and expert opinions in written, audio, and video formats`,
  emailPlaceholder: "Enter your email",
  emailSubmit: "Subscribe",
  submitSuccess: `Thank you for subscribing!`,
  submitFailure: "Something went wrong. Please try again.",
} as const

type NewsletterFormProps = {
  data?: typeof defaultData
}

export function NewsletterForm({ data = defaultData }: NewsletterFormProps) {
  const [emailValue, setEmailValue] = useState("")

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<null | "success" | "failure">(null)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)

  const onSubmit = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      event?.preventDefault()

      if (isSubmitting) {
        return
      }

      setIsSubmitting(true)

      const email = emailValue.trim()

      if (!email || !email.trim() || !email.includes("@")) {
        setErrorMessage("Please enter a valid email address")
        setIsSubmitting(false)
        return
      }

      setErrorMessage(undefined)

      const response = await subscribeToNewsLetter(email)
      setIsSubmitting(false)

      const isInvalidEmail =
        typeof response === "object" &&
        "message" in response &&
        response.message === "Invalid email"

      if (typeof response === "object" && !isInvalidEmail) {
        setSubmitStatus("success")
        setTimeout(() => {
          setSubmitStatus(null)
        }, 3000)
      } else {
        setSubmitStatus("failure")
        setErrorMessage(
          isInvalidEmail
            ? "Please enter a valid email address"
            : "Something went wrong. Please try again.",
        )
        setTimeout(() => {
          setSubmitStatus(null)
        }, 3000)
      }
    },
    [emailValue, isSubmitting],
  )

  const fieldError = useMemo(() => {
    return (
      errorMessage ||
      (submitStatus === "success"
        ? data.submitSuccess
        : submitStatus === "failure"
          ? data.submitFailure
          : undefined)
    )
  }, [errorMessage, submitStatus, data])

  const _width = 18

  return (
    <form
      onSubmit={onSubmit}
      className="group animate-fade-in-up flex flex-col mt-0 lg:mt-[120px] p-0 lg:p-3 rounded-2xl"
      style={{
        animationDuration: "0.7s",
      }}
    >
      <Card className="size-10 rounded-lg p-0 flex items-center justify-center mb-4">
        <Icon as={Mailbox} className="text-fg-secondary size-5" />
      </Card>
      <MarketingHeading level={3} className="mb-2">
        Subscribe for onchain
        <br />
        fundamentals & updates
      </MarketingHeading>
      <div className="text-sm text-fg-secondary mb-6 text-pretty">
        Get curated, data-driven market overviews plus expert insights on standout protocols and
        companies.
      </div>
      {submitStatus === "success" ? (
        <div
          key="success"
          className="animate-fade-in-up flex items-center text-sm text-fg-emerald text-balance max-w-[800px] h-tt-regular"
          style={{ animationDuration: "0.3s" }}
        >
          {data.submitSuccess}
        </div>
      ) : (
        <div
          key="form"
          className="animate-fade-in-up flex flex-col md:flex-row flex-wrap gap-3"
          style={{
            animationDuration: "0.3s",
            ...(isSubmitting ? { opacity: 0.5 } : {}),
          }}
        >
          <TextField
            name="email"
            aria-label="Email"
            placeholder={data.emailPlaceholder}
            type="email"
            isDisabled={isSubmitting}
            size="medium"
            className="flex-1 min-w-[200px]"
            isInvalid={!!errorMessage || submitStatus === "failure"}
            errorMessage={fieldError}
            onChange={(value) => {
              setErrorMessage(undefined)
              setSubmitStatus(null)
              setEmailValue(value)
            }}
            autoComplete="email"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                onSubmit()
              }
            }}
          />
          <Button
            type="submit"
            isDisabled={isSubmitting}
            size="medium"
            variant="default"
            color="neutral"
            onPress={() => onSubmit()}
          >
            {data.emailSubmit}
          </Button>
        </div>
      )}
    </form>
  )
}
